<template>
  <section id="governance" class="o-section c-governance" v-my-section-class>

    <!-- Background -->
    <div class="o-background" v-if="$store.state.device !== 0">
      <div class="o-background__inner">
        <transition name="quick-fade">
          <div class="o-background__group o-background__group--circle" key="circle" v-if="tabIndex === 1">
            <my-circle class="o-background__item o-background__item--1" :image-file="'people/resilience'" :variant="2"></my-circle>
          </div>
          <div class="o-background__group o-background__group--default" key="default" v-else></div>
        </transition>
      </div>
    </div>

    <!-- Content -->
    <div class="o-section__container">
      <h2 class="o-section__title">Governance</h2>

      <my-tabs @tab-changed="setTabIndex">

        <!-- Tab 1 -->
        <my-tab title="Transparency" :is-active="true" class="o-tabs__tab--fixed-head">
          <div class="o-panel__header" v-my-fixed-title>
            <h3 class="o-panel__title">Being a transparent business</h3>
          </div>

          <my-slider>

            <!-- Slide -->
            <my-slide>
              <div class="o-panel__body">
                <p class="u-intro">We recognise that our role as a provider of a critical service brings with it both privilege and responsibility. As such, we want to be open with our stakeholders, encouraging close scrutiny of our operations, giving clear sight of matters such as our financial performance and how we reflect the diversity of the community we serve.</p>

                <p>Our financial returns are among the best in the industry. They reflect the industry-leading performance set out in this review. All our employees participate in our Company Incentive Plan. The plan is weighted, with 60% of the incentive based on non-financial performance (Safety, Customer Service and Reliability) and 40% based on our financial performance. This ensures that everyone in the organisation is focused on delivering for our customers.</p>

                <p>As we strive to become the best-performing DNO from 2015/16 to 2018/19, we are committed to diversity and inclusiveness. By embedding this in our culture we will serve our customers better and ensure we have access to the greatest pool of talent available. This year we have begun working towards the National Equality Standard, and we have the ambition to become accredited by the end of 2018.</p>
              </div>
              <div class="o-panel__footer">
                <div class="o-link__btn-group">
                  <my-download-button :section="'governance-transparency'"></my-download-button>
                </div>
              </div>
            </my-slide>

            <!-- Slide -->
            <my-slide>
              <div class="o-panel__body o-panel__body--full">
                <figure>
                  <figcaption class="o-chart__caption">Main Company Incentive Plan – Linking performance with delivery for customers</figcaption>
                  <div class="u-center">
                    <img :src="require('@/assets/images/transparency/pie.png')" :srcset="require('@/assets/images/transparency/pie.png') + ' 1x, ' + require('@/assets/images/transparency/pie@2x.png') + ' 2x'" width="512" height="293" alt="Being a transparent business">
                  </div>
                </figure>
              </div>
            </my-slide>

            <!-- Slide -->
            <my-slide>
              <div class="o-panel__body o-panel__body--full">

                <h4 class="o-chart__title">Gender Mix <span class="u-not-bold">% (Company level)</span></h4>
                <div class="o-grid o-grid--2 o-media--2">
                  <div class="o-grid__item">
                    <figure class="o-chart o-media__chart">
                      <img :src="require('@/assets/images/transparency/gender-mix.png')" :srcset="require('@/assets/images/transparency/gender-mix.png') + ' 1x, ' + require('@/assets/images/transparency/gender-mix@2x.png') + ' 2x'" width="512" height="220" alt="Being a transparent business">
                    </figure>
                  </div>
                  <div class="o-grid__item o-media__text">
                    <p>The lack of gender diversity in engineering workplaces is a widely recognised challenge. In its 2015 Skills Survey, the Institution of Engineering and Technology highlighted that females account for only 9% of all engineering and technology employees. Increasing the number of female engineers and apprentices remains a key objective of our diversity strategy.</p>
                  </div>
                </div>
              </div>
            </my-slide>

            <!-- Slide -->
            <my-slide>
              <div class="o-panel__body o-panel__body--full">
                <h4 class="o-chart__title">Incentive revenue £m <span>(2012/13 prices)</span></h4>
                <div class="o-grid o-grid--2 o-media--2">
                  <div class="o-grid__item">
                    <figure class="o-chart o-media__chart">
                      <img :src="require('@/assets/images/transparency/incentive-revenue.png')" :srcset="require('@/assets/images/transparency/incentive-revenue.png') + ' 1x, ' + require('@/assets/images/transparency/incentive-revenue@2x.png') + ' 2x'" width="512" height="220" alt="Being a transparent business">
                    </figure>
                  </div>
                  <div class="o-grid__item o-media__text">
                    <p>Under Ofgem’s regulatory regime for our industry, we can earn additional financial rewards by delivering improved service to our customers or be penalised for delivering poor service. The main areas where we can either earn additional rewards, or be penalised are: network reliability; customer service; how we engage with our stakeholders, in particular vulnerable customers; and the service we provide customers who want a new connection to our network.</p>
                  </div>
                </div>
              </div>
            </my-slide>

            <!-- Slide -->
            <my-slide>
              <div class="o-panel__body o-panel__body--full">
                <h4 class="o-chart__title">Investment in the network</h4>
                <div class="o-grid o-grid--2 o-media--2 o-media--2b">
                  <div class="o-grid__item">
                    <figure class="o-chart o-media__chart">
                      <img :src="require('@/assets/images/transparency/investment.png')" :srcset="require('@/assets/images/transparency/investment.png') + ' 1x, ' + require('@/assets/images/transparency/investment@2x.png') + ' 2x'" width="512" height="220" alt="Being a transparent business">
                    </figure>
                    <ul class="o-chart__key">
                      <li class="o-chart__key__item o-chart__key__item--orange">Operating cashflow (£m) (after interest and tax)</li>
                      <li class="o-chart__key__item o-chart__key__item--grey">Amount spent on the network (£m)</li>
                    </ul>
                  </div>
                  <div class="o-grid__item o-media__text">
                    <p>On average, we invest 85% of the cash generated from operations in the network.</p>
                  </div>
                </div>
              </div>
            </my-slide>
          </my-slider>
        </my-tab>

        <!-- Tab 1 -->
        <my-tab title="Resilience" class="o-tabs__tab--fixed-head">
          <div class="o-panel__header" v-my-fixed-title>
            <h3 class="o-panel__title">Being a resilient business</h3>
          </div>
          <my-slider>

            <!-- Slide -->
            <my-slide>
              <div class="o-panel__body">
                <p class="u-intro">In these days of increasing risks and threats from ever-changing technological, commercial and environmental challenges, it is vital to be more adaptable, flexible and agile. These characteristics are typical of a resilient organisation.</p>

                <p>Based on an initial study and report by the UK Government’s Emergency Planning College (EPC) in 2015, UK Power Networks has been developing a strategy and operational framework that brings together and strengthens all risk-related disciplines and activities. This ensures that we are prepared for the events – both planned and unforeseen – that may affect our ability to “keep the lights on”.</p>

                <p>Being a resilient business is relevant to all aspects of our operations. The scope ranges from ensuring that we can cope with extreme weather events to having access to sufficient resources, including staff, information technology and finance, to meet our customers’ needs on a day to day basis.</p>

                <p>This year we have significantly strengthened our governance structure to deliver and maintain a higher level of resilience across the entire organisation. There is corporate level responsibility overseeing the implementation of the strategy and framework for organisational resilience. This is delivered via the resilience committee, which is led by the Director of Network Operations. It is also responsible for providing assurance to the CEO and the Board that the required levels of organisational resilience are in place to support the delivery of the corporate strategic plan.</p>
              </div>
              <div class="o-panel__footer">
                <div class="o-link__btn-group">
                  <my-download-button :section="'governance-resilience'"></my-download-button>
                </div>
              </div>
            </my-slide>

            <!-- Slide -->
            <my-slide>
              <div class="o-panel__body o-panel__body--full">
                <h3 class="u-small-red">Strengthening our governance structure</h3>
                <div class="c-governance__structure">
                  <div class="c-governance__structure__item">
                    <figure class="c-governance__structure__image">
                      <img :src="require('@/assets/images/logos/structure-1.png')" :srcset="require('@/assets/images/logos/structure-1.png') + ' 1x, ' + require('@/assets/images/logos/structure-1@2x.png') + ' 2x'" width="512" height="220" alt="Being a transparent business">
                    </figure>
                    <div class="c-governance__structure__text">
                      <p>Resilience framework covers all aspects of our business</p>
                    </div>
                  </div>

                  <div class="c-governance__structure__item">
                    <figure class="c-governance__structure__image">
                      <img :src="require('@/assets/images/logos/structure-2.png')" :srcset="require('@/assets/images/logos/structure-2.png') + ' 1x, ' + require('@/assets/images/logos/structure-2@2x.png') + ' 2x'" width="512" height="220" alt="Being a transparent business">
                    </figure>
                    <div class="c-governance__structure__text">
                      <p>Seeking external accreditation</p>
                    </div>
                  </div>

                  <div class="c-governance__structure__item">
                    <figure class="c-governance__structure__image">
                      <img :src="require('@/assets/images/logos/structure-3.png')" :srcset="require('@/assets/images/logos/structure-3.png') + ' 1x, ' + require('@/assets/images/logos/structure-3@2x.png') + ' 2x'" width="512" height="220" alt="Being a transparent business">
                    </figure>
                    <div class="c-governance__structure__text">
                      <p>Corporate level accountability to oversee organisational resilience</p>
                    </div>
                  </div>
                </div>
              </div>
            </my-slide>

            <!-- Slide -->
            <my-slide>
              <div class="o-panel__body o-panel__body--full">
                <div class="o-grid o-grid--2">
                  <div class="o-grid__item">
                    <figure>
                      <img :src="require('@/assets/images/cabinet-office.png')" :srcset="require('@/assets/images/cabinet-office.png') + ' 1x, ' + require('@/assets/images/cabinet-office@2x.png') + ' 2x'" width="512" height="220" alt="Cabinet Office">
                    </figure>
                  </div>
                  <div class="o-grid__item">
                    <h3 class="u-small-red">Extract from the EPC’s Second Base Line Review of UK Power Networks’ Organisational Resilience</h3>
                    <p>July 2017</p>

                    <p>Due to the nature of the business and its focus on health and safety, and in particular to “keeping the lights on” for its customers, there is a clear strength in its ability to plan for and respond to power disruption situations. This is particularly relevant to weather related events e.g. storms.</p>

                    <p>The most significant ‘step change’ in the company’s level of Organisational Resilience lies in the area of governance and business continuity. Evidence of key BCM building blocks is in place as well as the establishment of the Organisational Resilience Leadership Team.</p>
                  </div>
                </div>
              </div>
            </my-slide>

            <!-- Slide -->
            <my-slide>
              <div class="o-panel__body o-panel__body--full">
                <div class="o-grid o-grid--2">
                  <div class="o-grid__item">
                    <figure>
                      <img :src="require('@/assets/images/bis.png')" :srcset="require('@/assets/images/bis.png') + ' 1x, ' + require('@/assets/images/bis@2x.png') + ' 2x'" width="512" height="220" alt="BIS">
                    </figure>
                  </div>
                  <div class="o-grid__item">
                    <h3 class="u-small-red">First in UK</h3>

                    <p>UK Power Networks is the first distribution network operator to go through the Cabinet Office, Emergency Planning College assessment for Organisational Resilience based on BS 65000:2014 Guidance on Organisational Resilience.</p>

                  </div>
                </div>
              </div>
            </my-slide>

          </my-slider>
        </my-tab>

        <!-- Tab 2 -->
        <my-tab title="Board of Directors" class="o-tabs__tab--fixed-head">
          <div class="o-panel__header">
            <h3 class="o-panel__title">Board of Directors</h3>
          </div>

          <my-slider>
            <!-- SLIDE -->
            <my-slide :key="index" v-for="(group, index) in groupedDirectors">
              <div class="o-panel__body o-panel__body--full o-grid o-grid--3">
                <div class="o-grid__item c-person" v-for="(item, index) in group">
                  <figure class="c-person__image">
                    <my-circle :image-file="'board/' + item.slug"></my-circle>
                  </figure>
                  <h4 class="c-person__title">{{ item.title }}</h4>
                  <div class="c-person__position" v-html="item.position"></div>
                  <div class="c-person__body" v-html="item.body"></div>
                </div>
              </div>
              <div class="o-panel__footer">
                <div class="o-link__btn-group">
                  <my-download-button :section="'governance-board'"></my-download-button>
                </div>
              </div>
            </my-slide>
          </my-slider>
        </my-tab>

        <!-- Tab 3 -->
        <my-tab title="Executive Management team" class="o-tabs__tab--fixed-head">
          <div class="o-panel__header" v-my-fixed-title>
            <h3 class="o-panel__title">Executive Management team</h3>
          </div>

          <my-slider>
            <!-- SLIDE -->
            <my-slide :key="index" v-for="(group, index) in groupedManagement">
              <div class="o-panel__body o-panel__body--full o-grid o-grid--3">
                <div class="o-grid__item c-person" v-for="(item, index) in group">
                  <figure class="c-person__image">
                    <my-circle :image-file="'board/' + item.slug"></my-circle>
                  </figure>
                  <h4 class="c-person__title">{{ item.title }}</h4>
                  <div class="c-person__position" v-html="item.position"></div>
                  <div class="c-person__body" v-html="item.body"></div>
                </div>
              </div>
              <div class="o-panel__footer">
                <div class="o-link__btn-group">
                  <my-download-button :section="'governance-management'"></my-download-button>
                </div>
              </div>
            </my-slide>
          </my-slider>
        </my-tab>
      </my-tabs>
    </div>
  </section>
</template>

<script>
import { MySectionClass } from '@/directives/my-section-class'
import { MyFixedTitle } from '@/directives/my-fixed-title'
import { MyTabs, MyTab, MyTabInner } from '@/components/standard/my-tabs'
import { MySlider, MySlide } from '@/components/standard/my-slider'
import MyTable from '@/components/standard/my-table'
import MyCircle from '@/components/standard/my-circle'
import MyDownloadButton from '@/components/standard/my-download-button'
import mixins from '@/mixins'

export default {
  name: 'my-governance',
  components: {
    MyTabs,
    MyTab,
    MyTabInner,
    MySlider,
    MySlide,
    MyTable,
    MyCircle,
    MyDownloadButton
  },
  directives: {
    MySectionClass,
    MyFixedTitle
  },
  mixins: [mixins],
  computed: {
    numberPerGroup () {
      return (this.$store.state.device !== 0) ? 3 : 1
    },
    boardItems () {
      return this.$store.state.content['board'] || []
    },
    groupedDirectors () {
      let origArray = (this.boardItems.length > 0) ? this.boardItems[0].slice() : []
      let arrays = []
      while (origArray.length > 0) {
        arrays.push(origArray.splice(0, this.numberPerGroup))
      }
      return arrays
    },
    groupedManagement () {
      let origArray = (this.boardItems.length > 0) ? this.boardItems[1].slice() : []
      let arrays = []
      while (origArray.length > 0) {
        arrays.push(origArray.splice(0, this.numberPerGroup))
      }
      return arrays
    }
  }
}
</script>



// WEBPACK FOOTER //
// MyGovernance.vue?5c3f176f